/**
 * Matchmaker de 2 humanos (M3, MVP). DOM-free y testeable sin sockets.
 *
 * Empareja jugadores en **salas autoritativas compartidas** (`GameRoom` con
 * `bot:false` → dos slots humanos). `quickJoin` mete al jugador en la sala que
 * espera rival o crea una nueva. El adaptador de transporte (WebSocket/WebTransport)
 * solo enruta mensajes; el emparejamiento y la autoridad viven aquí.
 *
 * Escala a matchmaking regional/por MMR sustituyendo este componente por
 * Colyseus matchMaker u Open Match, sin tocar `GameRoom`/`AuthoritativeServer`.
 */
import { MapConfig, MatchRules, mapById, defaultRules } from "../game";
import { GameRoom } from "./room";
import { ServerMessage, RoomPhase } from "./wire";

export interface MatchmakerOptions {
  map?: MapConfig;
  rules?: MatchRules;
  /**
   * Si un jugador solo espera rival humano más de este tiempo (ms), se le da un
   * bot server-side en su propia sala. `0` o negativo desactiva el fallback.
   */
  botFallbackMs?: number;
  /** Reloj inyectable (para tests deterministas). Por defecto `Date.now`. */
  now?: () => number;
  /** Región por defecto de las salas (etiqueta; hosting regional real es M3). */
  defaultRegion?: string;
  /**
   * Margen de gracia (ms) que una sala VACÍA sobrevive antes de destruirse.
   *
   * Sin esto, al jugador que se queda solo en su sala y pierde la conexión un
   * segundo (túnel, cambio de wifi a datos) se le borra la sala mientras
   * reconecta, y vuelve a una sala DISTINTA con el link de invitación ya
   * repartido apuntando a la nada. Con el margen, reconectar dentro de la
   * ventana le devuelve SU sala. `0` restaura el borrado inmediato.
   */
  emptyRoomGraceMs?: number;
}

export interface JoinResult {
  roomId: string;
  room: GameRoom;
  slot: number;
  welcome: ServerMessage;
}

/** Motivo por el que un intento de unión a sala concreta es rechazado. */
export type JoinReject = "bad-password" | "room-full";

/** Resultado de un intento de unión: éxito (`JoinResult`) o rechazo. */
export type JoinOutcome = JoinResult | { rejected: JoinReject };

/** Opciones de creación de sala desde el lobby (config del host). */
export interface CreateRoomOptions {
  /** Nombre visible; se recorta y usa uno por defecto si viene vacío. */
  name?: string;
  /** Contraseña; vacío = sala abierta. */
  password?: string;
  /** Si `false`, la sala NO aparece en la lista pública (solo por enlace/id). */
  listed?: boolean;
  /** Cancha (id de MAPS); por defecto la del matchmaker. */
  mapId?: string;
  /** Límite de goles (0 = ilimitado). */
  goalLimit?: number;
  /** Límite de tiempo en segundos (0 = ilimitado). */
  timeLimit?: number;
  /** Región donde hostear (etiqueta). */
  region?: string;
  /** Nombre visible del jugador que crea la sala (para la lista de jugadores). */
  playerName?: string;
  /** Capacidad de la sala (nº de jugadores). Por defecto `DEFAULT_CAPACITY` (8). */
  capacity?: number;
  /**
   * Si `true`, la sala nace ya EN JUEGO en vez de en el lobby.
   *
   * Es lo que necesita la "partida rápida": sin esto, el cliente tenía que
   * conectarse, esperar el estado `lobby` y pedir `start` — una vuelta entera a
   * la red antes de poder jugar, que en una conexión normal son cientos de
   * milisegundos de pantalla muerta.
   */
  autoStart?: boolean;
}

/**
 * Capacidad de una sala: mínimo, máximo y valor por defecto.
 *
 * El máximo (30) es el techo del roster de una sala creada a mano; el
 * emparejamiento rápido sigue formando parejas (`QUICK_CAPACITY`), porque
 * esperar a que se junten treinta desconocidos no es "rápido".
 */
export const MIN_CAPACITY = 2;
export const MAX_CAPACITY = 30;
export const DEFAULT_CAPACITY = 8;
/** Tamaño por defecto del emparejamiento rápido. */
export const QUICK_CAPACITY = 2;

/** Acota una capacidad pedida por el cliente al rango soportado. */
export function clampCapacity(n: number | undefined, fallback = DEFAULT_CAPACITY): number {
  const v = Math.floor(Number.isFinite(n as number) ? (n as number) : fallback);
  return Math.max(MIN_CAPACITY, Math.min(MAX_CAPACITY, v));
}

/** MM-2 · Opciones de emparejamiento rápido: qué partida está pidiendo. */
export interface QuickJoinOptions {
  /** Región en la que quiere jugar. Por defecto, la del servidor. */
  region?: string;
  /** Tamaño de partida (nº de jugadores). Por defecto `QUICK_CAPACITY` (2). */
  capacity?: number;
}

/** MM-2 · Estado de una cola de emparejamiento, para el lobby. */
export interface QueueSnapshot {
  region: string;
  capacity: number;
  /** Cuántos esperan ya en la sala de esta cola. */
  waiting: number;
  /** Cuánto lleva esperando esta cola (ms). */
  waitingMs: number;
  /** Espera típica reciente (ms), o `null` si aún no hay historia. */
  estimatedWaitMs: number | null;
}

/**
 * Cuántas esperas recientes se recuerdan para calcular la ETA. Corta a
 * propósito: lo que se tardó hace una hora no dice nada de lo que se va a
 * tardar ahora, y arrastrarlo hace que la estimación reaccione tarde.
 */
const WAIT_SAMPLES = 20;

interface RoomMeta {
  name: string;
  password: string;
  listed: boolean;
  region: string;
  /** Ver `RoomInfo.kind`. */
  kind: RoomKind;
}

/**
 * Qué clase de partido es una sala.
 *
 * - `official`: la creó el EMPAREJAMIENTO (partida rápida). El servidor elige
 *   sala, región y rivales; es la vía "de verdad", la que algún día llevará
 *   progreso o clasificación.
 * - `friendly`: la creó una PERSONA para jugar con quien quiera, en la región
 *   que ella eligió — normalmente la suya, para que su gente entre con buen
 *   ping. Es el equivalente al "hostea tu sala" de Haxball con la infraestructura
 *   de este juego: no hay servidor casero, pero sí sala propia en el edge más
 *   cercano. Se anuncian como PARTIDOS NO OFICIALES para que nadie confunda un
 *   amistoso con una partida competitiva.
 */
export type RoomKind = "official" | "friendly";

export interface RoomInfo {
  id: string;
  players: number;
  capacity: number;
  status: "waiting" | "full";
  name: string;
  hasPassword: boolean;
  /** Si `false`, no debe publicarse en la lista pública. */
  listed: boolean;
  region: string;
  /** Cancha en la que se juega. La lista lo publicaba fijo como "classic", así
   *  que filtrar por mapa en el lobby no encontraba nada que no fuese esa. */
  mapId: string;
  /** Fase de la sala: distingue "esperando en el lobby" de "partida en curso",
   *  que para quien busca sala no es lo mismo aunque las dos tengan sitio. */
  phase: RoomPhase;
  /** Oficial (emparejamiento) o amistosa creada por un jugador. Ver `RoomKind`. */
  kind: RoomKind;
}

const MAX_ROOM_NAME = 24;

export class Matchmaker {
  private readonly rooms = new Map<string, GameRoom>();
  private readonly meta = new Map<string, RoomMeta>();
  private readonly clientRoom = new Map<string, string>();
  /**
   * MM-2 · Sala en espera POR COLA, en vez de una sola global.
   *
   * Con una única sala en espera, dos personas que piden cosas distintas —otra
   * región, una sala de 4— acababan emparejadas igualmente o se pisaban la cola
   * la una a la otra. La clave separa lo que NO es intercambiable; dentro de una
   * misma clave, cualquiera vale contra cualquiera.
   */
  private readonly waiting = new Map<string, { roomId: string; since: number }>();
  /**
   * Duración de los últimos emparejamientos, para poder dar una ETA honesta.
   * Se guarda una ventana corta: lo que tardó ayer no dice nada de ahora.
   */
  private readonly recentWaits: number[] = [];
  private nextId = 1;
  private readonly now: () => number;
  private readonly botFallbackMs: number;
  private readonly defaultRegion: string;
  private readonly emptyRoomGraceMs: number;
  /** roomId → instante en que se quedó vacía (ventana de reconexión). */
  private readonly emptySince = new Map<string, number>();

  constructor(private readonly opts: MatchmakerOptions = {}) {
    this.now = opts.now ?? (() => Date.now());
    this.botFallbackMs = opts.botFallbackMs ?? 0;
    this.defaultRegion = opts.defaultRegion ?? "us-east";
    this.emptyRoomGraceMs = opts.emptyRoomGraceMs ?? 0;
  }

  /**
   * MM-2 · Clave de cola: qué NO es intercambiable al emparejar.
   *
   * Región y tamaño de partida. Dentro de una misma clave cualquiera vale
   * contra cualquiera; entre claves distintas, juntarlos es darle a alguien una
   * partida que no pidió.
   */
  private queueKey(region: string, capacity: number): string {
    return `${region}|${capacity}`;
  }

  /** Empareja a un cliente: entra a la sala en espera de SU cola o crea una. */
  quickJoin(
    clientId: string,
    playerName?: string,
    identity?: string,
    opts: QuickJoinOptions = {},
  ): JoinResult {
    const region = opts.region || this.defaultRegion;
    const capacity = clampCapacity(opts.capacity, QUICK_CAPACITY);
    const key = this.queueKey(region, capacity);

    // 1) ¿hay una sala esperando rival en ESTA cola?
    const pending = this.waiting.get(key);
    if (pending) {
      const room = this.rooms.get(pending.roomId);
      if (room && !room.isFull) {
        const welcome = room.addClient(clientId, playerName, identity);
        if (welcome && welcome.t === "welcome") {
          const roomId = pending.roomId;
          this.clientRoom.set(clientId, roomId);
          this.emptySince.delete(roomId); // vuelve a estar habitada
          if (room.isFull) {
            // Cola resuelta: se anota lo que tardó, que es lo que alimenta la
            // ETA que se le enseña al siguiente.
            this.recordWait(this.now() - pending.since);
            this.waiting.delete(key);
          }
          return { roomId, room, slot: welcome.slot, welcome };
        }
      }
      this.waiting.delete(key); // sala inválida/llena: descártala
    }

    // 2) crear sala nueva y quedar en espera en esta cola
    const roomId = `r${this.nextId++}`;
    const room = new GameRoom({
      players: capacity, bot: false, map: this.opts.map, rules: this.opts.rules,
    });
    const welcome = room.addClient(clientId, playerName, identity);
    this.rooms.set(roomId, room);
    // Emparejamiento rápido: sala OFICIAL (la monta el servidor, no una persona).
    this.meta.set(roomId, { name: `Sala ${roomId}`, password: "", listed: true, region, kind: "official" });
    this.clientRoom.set(clientId, roomId);
    this.waiting.set(key, { roomId, since: this.now() });
    // addClient de una sala nueva siempre da slot 0.
    const slot = welcome && welcome.t === "welcome" ? welcome.slot : 0;
    return { roomId, room, slot, welcome: welcome ?? { t: "welcome", slot: 0, tick: 0 } };
  }

  /** Anota lo que tardó un emparejamiento (ventana corta para la ETA). */
  private recordWait(ms: number): void {
    if (!Number.isFinite(ms) || ms < 0) return;
    this.recentWaits.push(ms);
    if (this.recentWaits.length > WAIT_SAMPLES) this.recentWaits.shift();
  }

  /**
   * MM-2 · Estado de las colas, para que el lobby pueda decir algo mejor que
   * "buscando…": cuánta gente espera y cuánto se tardó las últimas veces.
   *
   * La ETA sale de la MEDIANA de las esperas recientes y no de la media: una
   * sola espera larguísima (el que se quedó solo de madrugada) desplaza la media
   * lo suficiente como para prometerle cinco minutos a todo el mundo.
   */
  queueSnapshot(): QueueSnapshot[] {
    const eta = this.estimatedWaitMs();
    const out: QueueSnapshot[] = [];
    for (const [key, entry] of this.waiting) {
      const room = this.rooms.get(entry.roomId);
      if (!room) continue;
      const [region, capacity] = key.split("|");
      out.push({
        region,
        capacity: Number(capacity),
        waiting: room.clientCount,
        waitingMs: Math.max(0, this.now() - entry.since),
        estimatedWaitMs: eta,
      });
    }
    return out;
  }

  /** Mediana de las esperas recientes, o `null` si aún no hay historia. */
  estimatedWaitMs(): number | null {
    if (this.recentWaits.length === 0) return null;
    const sorted = [...this.recentWaits].sort((a, b) => a - b);
    return Math.round(sorted[Math.floor(sorted.length / 2)]);
  }

  /**
   * Crea una sala PROPIA con nombre/contraseña/visibilidad (creador = slot 0).
   * A diferencia de `quickJoin`, NO entra en la cola de emparejamiento rápido:
   * es de acceso explícito (aparece en la lista si `listed`, y se une por su id
   * — con contraseña si la tiene). No recibe bot por timeout (esperas a tu rival).
   */
  createRoom(clientId: string, opts: CreateRoomOptions = {}, identity?: string): JoinResult {
    const roomId = `r${this.nextId++}`;
    const map = opts.mapId ? mapById(opts.mapId) : this.opts.map;
    const base = defaultRules();
    const rules: MatchRules | undefined = (opts.goalLimit != null || opts.timeLimit != null)
      ? { goalLimit: opts.goalLimit ?? base.goalLimit, timeLimit: opts.timeLimit ?? base.timeLimit }
      : this.opts.rules;
    // Sala creada = arranca en LOBBY (el admin la configura y pulsa EMPEZAR),
    // salvo que se pida lo contrario (partida rápida: a jugar ya).
    const capacity = clampCapacity(opts.capacity);
    const room = new GameRoom({
      players: capacity, bot: false, map, rules,
      phase: opts.autoStart ? "playing" : "lobby",
    });
    const welcome = room.addClient(clientId, opts.playerName, identity);
    this.rooms.set(roomId, room);
    this.meta.set(roomId, {
      name: (opts.name?.trim() || `Sala ${roomId}`).slice(0, MAX_ROOM_NAME),
      password: opts.password ?? "",
      listed: opts.listed ?? true,
      region: opts.region || this.defaultRegion,
      // La monta una persona en la región que ha elegido: partido NO OFICIAL.
      kind: "friendly",
    });
    this.clientRoom.set(clientId, roomId);
    const slot = welcome && welcome.t === "welcome" ? welcome.slot : 0;
    return { roomId, room, slot, welcome: welcome ?? { t: "welcome", slot: 0, tick: 0 } };
  }

  /**
   * Une a un cliente a una sala CONCRETA (elegida en el lobby o por enlace).
   * Si la sala ya no existe, cae al emparejamiento rápido. Si tiene contraseña y
   * no coincide → rechazo `bad-password`; si está llena o juega vs bot →
   * `room-full`. La contraseña por defecto es vacía (sala abierta).
   */
  joinRoom(clientId: string, roomId: string, password = "", playerName?: string, identity?: string): JoinOutcome {
    const room = this.rooms.get(roomId);
    if (!room) return this.quickJoin(clientId, playerName, identity); // sala desaparecida → emparejamiento normal
    const meta = this.meta.get(roomId);
    if (meta && meta.password && meta.password !== password) return { rejected: "bad-password" };
    if (room.isFull || room.botEnabled) return { rejected: "room-full" };
    const welcome = room.addClient(clientId, playerName, identity);
    if (!welcome || welcome.t !== "welcome") return { rejected: "room-full" };
    this.clientRoom.set(clientId, roomId);
    this.emptySince.delete(roomId); // reconexión dentro de la ventana: la sala revive
    // Si alguien llenó por enlace la sala que estaba haciendo cola, esa cola ya
    // no tiene dónde meter al siguiente.
    if (room.isFull) this.unqueue(roomId);
    return { roomId, room, slot: welcome.slot, welcome };
  }

  /**
   * Entra a una sala como ESPECTADOR (solo lectura). `null` si la sala ya no
   * existe.
   *
   * No pide contraseña a propósito: lo que protege una contraseña es el derecho
   * a JUGAR en esa sala (y a hablar en su chat). Aun así solo se puede espectar
   * lo que se puede encontrar, y las salas no listadas no salen en la lista
   * pública — que es la misma frontera que ya tenían.
   */
  spectate(clientId: string, roomId: string, name?: string): { roomId: string; room: GameRoom; welcome: ServerMessage } | null {
    const room = this.rooms.get(roomId);
    if (!room) return null;
    const welcome = room.addSpectator(clientId, name);
    this.clientRoom.set(clientId, roomId);
    return { roomId, room, welcome };
  }

  leave(clientId: string): void {
    const roomId = this.clientRoom.get(clientId);
    if (!roomId) return;
    this.clientRoom.delete(clientId);
    const room = this.rooms.get(roomId);
    if (!room) return;
    room.removeClient(clientId);
    if (room.clientCount !== 0) return;
    // Sala vacía: deja de emparejar en ella en cualquier caso.
    this.unqueue(roomId);
    if (this.emptyRoomGraceMs > 0) {
      // Ventana de reconexión: la sala sigue existiendo un rato por si quien se
      // cayó vuelve con el mismo id (`?room=<id>`). La limpia `sweepEmptyRooms`.
      this.emptySince.set(roomId, this.now());
      return;
    }
    this.destroyRoom(roomId);
  }

  private destroyRoom(roomId: string): void {
    this.rooms.delete(roomId);
    this.meta.delete(roomId);
    this.emptySince.delete(roomId);
    this.unqueue(roomId);
  }

  /** Saca una sala de la cola en la que estuviera (si estaba en alguna). */
  private unqueue(roomId: string): void {
    for (const [key, entry] of this.waiting) {
      if (entry.roomId === roomId) { this.waiting.delete(key); return; }
    }
  }

  /**
   * Destruye las salas que llevan vacías más de `emptyRoomGraceMs` (nadie
   * reconectó). El servidor debe llamarlo periódicamente. Devuelve los ids
   * destruidos, para poder registrarlos.
   */
  sweepEmptyRooms(): string[] {
    if (this.emptyRoomGraceMs <= 0 || this.emptySince.size === 0) return [];
    const now = this.now();
    const gone: string[] = [];
    for (const [roomId, since] of this.emptySince) {
      if (now - since < this.emptyRoomGraceMs) continue;
      gone.push(roomId);
    }
    for (const roomId of gone) this.destroyRoom(roomId);
    return gone;
  }

  /**
   * Fallback por timeout: si la sala en espera lleva más de `botFallbackMs` con
   * un solo jugador, le activa un bot server-side y la libera de la cola (deja
   * de esperar rival humano; el siguiente jugador abre una sala nueva). El
   * servidor debe llamar a esto periódicamente. Devuelve las salas convertidas.
   */
  sweepBotFallback(): { id: string; room: GameRoom }[] {
    if (this.botFallbackMs <= 0 || this.waiting.size === 0) return [];
    const now = this.now();
    const converted: { id: string; room: GameRoom }[] = [];
    // MM-2 · Se barren TODAS las colas, no solo una. Con la sala en espera única
    // de antes, quien esperaba en una región distinta a la de la última sala
    // creada no recibía bot jamás: su espera no la vigilaba nadie.
    for (const [key, entry] of [...this.waiting]) {
      if (now - entry.since < this.botFallbackMs) continue;
      const room = this.rooms.get(entry.roomId);
      if (!room || room.clientCount !== 1) {
        this.waiting.delete(key); // sala inválida o ya no está sola: deja de vigilarla
        continue;
      }
      room.enableBotFallback();
      // Ya es partida vs bot: el próximo jugador de esta cola abre otra sala.
      this.waiting.delete(key);
      converted.push({ id: entry.roomId, room });
    }
    return converted;
  }

  roomOf(clientId: string): { id: string; room: GameRoom } | null {
    const roomId = this.clientRoom.get(clientId);
    if (!roomId) return null;
    const room = this.rooms.get(roomId);
    return room ? { id: roomId, room } : null;
  }

  /** Salas activas para el bucle de tick del servidor. */
  activeRooms(): { id: string; room: GameRoom }[] {
    return [...this.rooms.entries()].map(([id, room]) => ({ id, room }));
  }

  /** Lista para el lobby (la fuente real de salas). Incluye salas no
   *  listadas; es el consumidor (el server en `GET /rooms`) quien filtra por
   *  `listed` para la lista pública. Las salas en ventana de reconexión (vacías,
   *  esperando a que su ocupante vuelva) NO se listan: son fantasmas para
   *  cualquiera que no traiga su enlace. */
  list(): RoomInfo[] {
    return [...this.rooms.entries()]
      .filter(([id]) => !this.emptySince.has(id))
      .map(([id, room]) => {
        const m = this.meta.get(id);
        return {
          id,
          players: room.clientCount,
          capacity: room.maxPlayers,
          status: room.isFull ? "full" : "waiting",
          name: m?.name ?? `Sala ${id}`,
          hasPassword: !!(m && m.password),
          listed: m?.listed ?? true,
          region: m?.region ?? this.defaultRegion,
          mapId: room.authoritative.game.mapId,
          phase: room.roomPhase,
          kind: m?.kind ?? "official",
        };
      });
  }
}
